import React, { useCallback, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import swal from "sweetalert";

import AssignmentIndOutlinedIcon from "@material-ui/icons/AssignmentIndOutlined";
import CardGiftcardOutlinedIcon from "@material-ui/icons/CardGiftcardOutlined";
import CastForEducationOutlinedIcon from "@material-ui/icons/CastForEducationOutlined";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import EmojiObjectsOutlinedIcon from "@material-ui/icons/EmojiObjectsOutlined";
import EventAvailableOutlinedIcon from "@material-ui/icons/EventAvailableOutlined";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import GroupWorkOutlinedIcon from "@material-ui/icons/GroupWorkOutlined";
import LibraryBooksOutlinedIcon from "@material-ui/icons/LibraryBooksOutlined";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import LoopIcon from "@material-ui/icons/Loop";
import OndemandVideoOutlinedIcon from "@material-ui/icons/OndemandVideoOutlined";
import PeopleOutlinedIcon from "@material-ui/icons/PeopleOutlined";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import SchoolOutlinedIcon from "@material-ui/icons/SchoolOutlined";
import StorefrontOutlinedIcon from "@material-ui/icons/StorefrontOutlined";
import TableChartOutlinedIcon from "@material-ui/icons/TableChartOutlined";

import EPICENTERLOGO from "../../assets/images/EpicenterWhite.png";
import { FLAGS, useFeatureFlags } from "../../featureFlags";
import Tooltip from "../../UI/Tooltip/Tooltip";
import { decrypt } from "../../utils/others";
import { useHasPermission } from "../../utils/usePermission";
import { SvgIcons } from "../SharedComponents/SvgIcons/SvgIcons";
import styles from "./SideNavbar.module.css";

/** Remembered across sessions — a rail the user chose should stay chosen. */
const COLLAPSED_KEY = "sidebarCollapsed";

/**
 * The nav, in display order.
 *
 * `allow` is a predicate rather than a precomputed boolean so the whole table
 * can live at module scope: it is static data, and rebuilding it on every
 * render only to read `.allow` off it was hiding that.
 *
 * ⚠️ **Every `allow` here is carried over verbatim from the previous version.**
 * They are the only thing deciding who sees which section, so a "tidier"
 * equivalent is a permissions change wearing a refactor's clothes.
 */
const NAV_ITEMS = [
  { text: "Dashboard", to: "/mentorDashboard", Icon: DashboardOutlinedIcon,
    allow: ({ roles }) => roles?.role === "mentor" },
  { text: "Demos", to: "/demos", Icon: CastForEducationOutlinedIcon,
    allow: ({ roles }) => roles?.role !== "affiliate_partner" },
  { text: "Slots", to: "/slots", Icon: EventAvailableOutlinedIcon,
    allow: ({ roles }) => roles?.role !== "affiliate_partner" },
  { text: "Orders", to: "/classes", Icon: ReceiptOutlinedIcon,
    allow: ({ roles }) => roles?.role !== "affiliate_partner" },
  { text: "Shift Management", to: "/mentor-shifts", iconName: "shift_management_icon",
    allow: ({ roles, can, isFlagOn }) =>
      roles?.isBDManager === 0 &&
      can("mentorShifts") &&
      isFlagOn(FLAGS?.EPICENTER_MENTOR_SHIFT_MANAGEMENT?.name) },
  { text: "Loop", to: "/loop", Icon: LoopIcon,
    allow: ({ roles }) =>
      roles?.role === "admin" ||
      roles?.role === "cs_person" ||
      roles?.role === "Moderator" },
  { text: "Courses", to: "/courses", Icon: LibraryBooksOutlinedIcon,
    allow: ({ can }) => can("coursesPage") },
  { text: "Promotions", to: "/promotions", Icon: LocalOfferOutlinedIcon,
    allow: ({ can }) => can("promotions") },
  { text: "Users", to: "/users", Icon: PeopleOutlinedIcon,
    allow: ({ roles, can }) => roles?.isBDManager === 0 && can("userPage") },
  { text: "Storefront", to: "/storefront", Icon: StorefrontOutlinedIcon,
    allow: ({ roles }) => roles?.role === "admin" },
  { text: "Community", to: "/community", Icon: ForumOutlinedIcon,
    allow: ({ can }) => can("Community") },
  { text: "Alchemy", to: "/alchemy", Icon: EmojiObjectsOutlinedIcon,
    allow: ({ can }) => can("alchemy") },
  { text: "Bootcamp", to: "/bootcamp", Icon: SchoolOutlinedIcon,
    allow: ({ can }) => can("bootCamp") },
  { text: "Affiliate Demos", to: "/affiliateDemos", Icon: GroupWorkOutlinedIcon,
    allow: ({ roles }) => roles?.role === "affiliate_partner" },
  { text: "Time table", to: "/timetable", Icon: TableChartOutlinedIcon,
    allow: ({ roles }) => roles?.role === "mentor" },
  { text: "Profile", to: "/mentors", Icon: AssignmentIndOutlinedIcon,
    allow: ({ roles }) => roles?.role === "mentor" },
  { text: "Events", to: "/events", Icon: EventOutlinedIcon,
    allow: ({ can }) => can("events") },
  { text: "Referrals", to: "/referral", Icon: CardGiftcardOutlinedIcon,
    allow: ({ can }) => can("referral") },
  { text: "Mentor tutorial", to: "/mentor-lms", Icon: OndemandVideoOutlinedIcon,
    allow: ({ can }) => can("mentorLMS") },
];

/**
 * The admin / ops sidebar, collapsible to an icon rail.
 *
 * Collapsed, the label is gone and the icon is all that identifies a
 * destination — so every row carries a Tooltip in that state. It is the shared
 * `UI/Tooltip`, which portals, because the nav is a scrolling `overflow: hidden`
 * column and anything rendered inline would be clipped by it.
 *
 * The width is published as a CSS variable on the element and consumed by the
 * layout in App.jsx. The old markup had two numbers for one measurement — a
 * drawer at `16.5%` inside a grid column at `16.67%` — which is why the rail
 * and the content edge never quite lined up.
 */
const SideNavbar = () => {
  const location = useLocation();
  const hasPermission = useHasPermission();
  const isFlagOn = useFeatureFlags();
  const roles = decrypt(localStorage.getItem("user"));

  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_KEY) === "true",
  );

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);

  const logOut = useCallback(() => {
    swal({
      title: "Logout",
      text: "Do you really want to logout?",
      buttons: ["No", "Yes, Logout"],
      closeOnClickOutside: false,
      closeOnEsc: false,
    }).then((logout) => {
      if (logout) {
        localStorage.removeItem("user");
        localStorage.removeItem("workShift");
        window.location.replace("/");
      }
    });
  }, []);

  const visibleItems = NAV_ITEMS.filter((item) =>
    item?.allow({ roles, can: hasPermission, isFlagOn }),
  );

  return (
    <aside
      className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ""}`}
      aria-label="Main navigation"
    >
      <div className={styles.header}>
        <img className={styles.logo} src={EPICENTERLOGO} alt="Epicenter" />
        <img className={styles.mark} src={EPICENTERLOGO} alt="Epicenter" />
        {/* The control names the ACTION, not the state — "Collapse sidebar"
            while it is open. `aria-expanded` carries the state itself. */}
        <Tooltip
          text={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          position="right"
        >
          <button
            type="button"
            className={styles.toggle}
            onClick={() => setIsCollapsed((open) => !open)}
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronRightIcon fontSize="small" />
            ) : (
              <ChevronLeftIcon fontSize="small" />
            )}
          </button>
        </Tooltip>
      </div>

      <nav className={styles.nav}>
        {visibleItems?.map(({ text, to, Icon, iconName }) => {
          const isActive = location?.pathname?.includes(to);
          return (
            // Only when collapsed: with the label visible the tooltip would just
            // repeat it, and a hint that restates what is already on screen is
            // noise that teaches people to ignore the ones that matter.
            //
            // `aria-label` is NOT the same hint by another name. Collapsed,
            // `.label` is `display: none` and the icon is aria-hidden, so the
            // row has no accessible name at all — and the tooltip cannot supply
            // one, being both mouse-only and visual-only. Expanded, the visible
            // label already names the row, so the attribute drops off.
            <Tooltip
              key={to}
              text={isCollapsed ? text : ""}
              position="right"
              customStyles={{ wrapper: styles.tooltipWrapper }}
            >
              <NavLink
                to={to}
                className={`${styles.link} ${isActive ? styles.active : ""}`}
                aria-current={isActive ? "page" : undefined}
                aria-label={isCollapsed ? text : undefined}
              >
                <span className={styles.icon} aria-hidden="true">
                  {iconName ? (
                    <SvgIcons
                      name={iconName}
                      width="18"
                      height="18"
                      className={styles.spriteIcon}
                    />
                  ) : (
                    <Icon fontSize="small" />
                  )}
                </span>
                <span className={styles.label}>{text}</span>
              </NavLink>
            </Tooltip>
          );
        })}
      </nav>

      <div className={styles.footer}>
        {/* Same two rules as the nav rows above: an accessible name that
            survives the collapsed state, and `tooltipWrapper` so Tooltip's
            `inline-flex` wrapper does not shrink-wrap a `width: 100%` button. */}
        <Tooltip
          text={isCollapsed ? "Logout" : ""}
          position="right"
          customStyles={{ wrapper: styles.tooltipWrapper }}
        >
          <button
            type="button"
            className={styles.logout}
            onClick={logOut}
            aria-label={isCollapsed ? "Logout" : undefined}
          >
            <ExitToAppIcon fontSize="small" />
            <span className={styles.logoutLabel}>Logout</span>
          </button>
        </Tooltip>
      </div>
    </aside>
  );
};

export default SideNavbar;
